import React from 'react';

import {
  useMediaQuery, useTheme,
} from '@mui/material';

import {
  DesktopSelector, Dropdown, DropdownIcon, DropdownItem, MobileSelector, Tab, TabText,
} from './index.styles';

export type TabOption<KeyType = string> = {
  key: KeyType,
  label: string | React.ReactElement | React.ReactElement[],
  icon?: React.ReactElement | React.ReactElement[],
}

interface TabsProps {
  options: TabOption[]
  selectedKey: string,
  onChange?: (newKey: string) => void,
  showIcons?: boolean,
  mobileTabs?: boolean,
  desktopDropdown?: boolean,
  orientation?: 'vertical' | 'horizontal',
  color?: 'primary' | 'success' | 'error',
  justifyContent?: 'center' | 'flex-start' | 'flex-end',
  maxOptions?: number,
  maxMobileOptions?: number,
  dense?: boolean,
}

const MAX_OPTIONS_LENGTH = 5;
const MAX_MOBILE_OPTIONS_LENGTH = 2;

const Tabs = ({ options,
  selectedKey,
  mobileTabs = true,
  desktopDropdown = false,
  showIcons = true,
  maxOptions = MAX_OPTIONS_LENGTH,
  maxMobileOptions = MAX_MOBILE_OPTIONS_LENGTH,
  onChange = () => {},
  justifyContent = 'center',
  color = 'primary',
  orientation = 'horizontal',
  dense = false }: TabsProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  if (options?.length === 0) return null;

  return (((isMd && !desktopDropdown && options.length <= maxOptions)
    || (!isMd && !desktopDropdown && mobileTabs && options.length <= maxMobileOptions)
    || (orientation === 'vertical' && !desktopDropdown)) ? (
      <DesktopSelector
        $orientation={orientation}
        $justifyContent={justifyContent}
        $dense={dense}
      >
        {options.map((opt) => (
          <Tab
            key={opt.key}
            onClick={() => onChange(opt.key)}
          >
            <TabText
              variant="subtitle1"
              $selected={opt.key === selectedKey}
              $color={color}
            >
              {showIcons && opt.icon}
              {opt.label}
            </TabText>
          </Tab>
        ))}
      </DesktopSelector>
    ) : (
      <MobileSelector
        $justifyContent={justifyContent}
        $dense={dense}
      >
        <Dropdown
          value={selectedKey}
          onChange={(e) => onChange(e.target.value as string)}
          IconComponent={DropdownIcon}
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 1,
                borderRadius: 2,
              },
            },
            sx: {
              '& li.Mui-selected': {
                backgroundColor: `${theme.palette[color].light}30`,
                color: theme.palette[color].dark,
                fontWeight: '600',
              },
            },
          }}
        >
          {options.map((opt) => (
            <DropdownItem
              key={opt.key}
              selected={options.indexOf(opt) === 0}
              value={opt.key}
            >
              {showIcons && opt.icon}
              {opt.label}
            </DropdownItem>
          ))}
        </Dropdown>
      </MobileSelector>
    ));
};

export default Tabs;
